import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { useT } from '../lib/idioma.js';

import type { TrancaDeOrdem } from '../lib/tranca.js';
import { Cadeado, MotivoDaTranca } from './TrancaDeOrdem.js';

/**
 * ── A PÁGINA DO CURSO QUE ESPERA OUTRO (24/09/2026) ─────────────────────────
 *
 * A vitrine e a trilha já não deixam clicar num curso trancado. **Mas nenhuma
 * das duas é o único caminho até aqui**: o link direto, o favorito e o e-mail de
 * ontem entram pela rota do curso sem passar por prateleira nenhuma. Até hoje
 * essa pessoa era mandada direto para a primeira aula e recebia um 403 — a
 * frase estava certa e chegava **depois** do clique, que é a única coisa que a
 * R5-bis não perdoa.
 *
 * ## As duas perguntas que este desenho responde
 *
 * **1. A página inteira muda, ou só ganha um aviso?** Nem uma coisa nem outra:
 * ela **deixa de ser uma porta e passa a ser um índice**. O aviso vem primeiro,
 * antes da descrição, porque quem chegou por link direto precisa de saber antes
 * de rolar. A lista de aulas **fica inteira** — ela é a única coisa na tela que
 * diz o que a pessoa ganha ao cumprir a dependência, e escondê-la trocaria um
 * clique recusado por uma página que não explica o que está a guardar —, mas
 * **nenhuma linha é link**. Oferecer o clique que o servidor recusa é o cadeado
 * decorativo ao contrário.
 *
 * **2. E o redirecionamento?** Ele para. A página do curso manda para a aula
 * desde 04/09, e com tranca mandar é levar ao 403. Com o curso trancado o
 * `abrirEm` é ignorado e a lista fica — ver `PaginaDoCurso`, em `App.tsx`.
 *
 * ## O que este bloco NÃO faz
 *
 * Não repete o cadeado em cada linha da lista. A tranca é **do curso**, e é dita
 * uma vez, aqui em cima: sessenta cadeados numa lista não são sinal, são papel
 * de parede — e mentiriam sobre a espécie, porque cada linha tem o estado dela
 * (concluída, por abrir por data, trancada pela avaliação do módulo) e esse
 * continua a valer e a aparecer.
 */
export function AvisoDoCursoTrancado({
  tranca,
  areaSlug,
}: {
  tranca: TrancaDeOrdem;
  areaSlug: string;
}) {
  const t = useT();
  return (
    /* O cadeado ao lado da frase, e não numa sobrancelha por cima dela: a
       palavra «Trancado» acima de «Este curso ainda não abriu» seria o mesmo
       fato escrito duas vezes a 8px de distância. O desenho dá o sinal de
       relance; a frase dá o fato.

       E a abertura é a mesma da recusa da aula (`Esta aula ainda não abriu.`):
       duas telas que dizem a mesma coisa dizem-na com as mesmas palavras. */
    <AvisoNoTopoDoCurso
      data-vst-tranca="curso"
      glifo={<Cadeado tamanho={14} />}
      titulo={t('acesso.cursoNaoAbriu')}
    >
      <MotivoDaTranca tranca={tranca} areaSlug={areaSlug} />
    </AvisoNoTopoDoCurso>
  );
}

/**
 * A moldura do aviso no topo da página do curso — o lugar, não o motivo.
 *
 * É componente à parte desde 24/09/2026, quando o curso ganhou **data de
 * estreia** e apareceu um segundo aviso no mesmo sítio (`AvisoDoLancamento`).
 * O que os dois partilham é a chapa, o glifo ao lado da linha grande e a última
 * linha — **«As aulas abaixo abrem junto com o curso»** —, que é o que impede a
 * lista inerte de parecer quebrada: clicável ontem, morta hoje, sem nada a
 * ligar uma coisa à outra. Ela é o que transforma a lista de «o que não posso»
 * em «o que vem depois», e vale igual nas duas espécies de espera.
 *
 * `children` é o corpo, e é opcional de propósito: a tranca de ordem tem um
 * motivo a desenhar por baixo (a frase com o nome e as réguas), e a data de
 * estreia **não tem** — lá a linha grande já é o fato inteiro.
 */
export function AvisoNoTopoDoCurso({
  glifo,
  titulo,
  children,
  ...resto
}: {
  glifo: ReactNode;
  titulo: ReactNode;
  children?: ReactNode;
} & ComponentPropsWithoutRef<'section'>) {
  const t = useT();
  return (
    <section
      {...resto}
      className="mt-5 px-5 py-4"
      style={{ background: 'var(--superficie)', borderRadius: 'var(--raio)' }}
    >
      <p className="flex items-start gap-2 text-lg font-medium">
        <span className="mt-[5px] shrink-0" style={{ color: 'var(--texto-suave)' }}>
          {glifo}
        </span>
        {titulo}
      </p>
      {children && <div className="mt-2 pl-[22px]">{children}</div>}
      <p className="mt-3 pl-[22px] text-xs" style={{ color: 'var(--texto-suave)' }}>
        {t('vitrine.aulasAbremJunto')}
      </p>
    </section>
  );
}
